import React, { createContext, useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useConfirm } from '../design-system/ConfirmDialog';

type Boundary = { register: (id: string, dirty: boolean, scope: string) => () => void; allowNavigation: (leave: () => void) => void };
export const UnsavedDraftNavigationContext = createContext<Boundary | null>(null);

/** One blocker owns every SPA transition, including Link and browser history. */
export function UnsavedDraftNavigationProvider({ children }: { children: React.ReactNode }) {
  const entries = useRef(new Map<string, string>());
  const revision = useRef(0);
  const bypass = useRef(false);
  const pending = useRef(false);
  const confirmDialog = useConfirm();
  const register = useCallback((id: string, dirty: boolean, scope: string) => {
    if (dirty) entries.current.set(id, scope); else entries.current.delete(id);
    revision.current += 1;
    return () => { entries.current.delete(id); revision.current += 1; };
  }, []);
  const allowNavigation = useCallback((leave: () => void) => {
    bypass.current = true;
    try { leave(); } finally { bypass.current = false; }
  }, []);
  const blocker = useBlocker(() => !bypass.current && entries.current.size > 0);
  useEffect(() => {
    if (blocker.state !== 'blocked' || pending.current) return;
    pending.current = true;
    const epoch = revision.current;
    let active = true;
    void confirmDialog({ title: '変更が保存されていません', message: '手元の変更を破棄して移動しますか？', confirmLabel: '破棄して移動', cancelLabel: '編集を続ける', danger: true }).then(discard => {
      if (!active) return;
      if (discard && revision.current === epoch) blocker.proceed(); else blocker.reset();
    }).finally(() => { pending.current = false; });
    return () => { active = false; pending.current = false; };
  }, [blocker, confirmDialog]);
  return <UnsavedDraftNavigationContext.Provider value={{ register, allowNavigation }}>{children}</UnsavedDraftNavigationContext.Provider>;
}
